import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import Layout from '@/components/Layout';
import { COMPANIES, companyPath } from '@/lib/index';

function FadeUp({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 36 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.9, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export default function Companies() {
  return (
    <Layout>
      {/* Hero */}
      <section className="relative pt-40 pb-20 min-h-[55vh] flex items-center overflow-hidden bg-[#080808]">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1532581646393-e75be8f3d166?w=1600&q=85"
            alt="Our Companies"
            className="w-full h-full object-cover opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[rgba(8,8,8,0.5)] to-[#080808]" />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }}>
            <p className="eyebrow mb-5">The Portfolio</p>
            <h1 className="heading-display text-[clamp(3rem,7vw,6rem)] text-[#f5f5f5] max-w-3xl">
              Our <span className="text-gold italic">Companies</span>
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Companies grid */}
      <section className="py-16 bg-[#080808]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 space-y-4">
          {COMPANIES.map((company, i) => (
            <FadeUp key={company.id} delay={i * 0.1}>
              <div className={`flex flex-col lg:flex-row ${i % 2 !== 0 ? 'lg:flex-row-reverse' : ''} gap-0 overflow-hidden group`}>
                {/* Image */}
                <div className="flex-1 relative h-72 lg:h-[440px] overflow-hidden">
                  <img
                    src={company.heroImage}
                    alt={company.name}
                    className="w-full h-full object-cover transition-transform duration-[1s] group-hover:scale-105"
                    loading={i < 2 ? 'eager' : 'lazy'}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[rgba(8,8,8,0.5)] to-transparent" />
                </div>
                {/* Info */}
                <div className="flex-1 bg-[#0c0c0c] border border-[rgba(201,168,76,0.08)] p-10 lg:p-16 flex flex-col justify-center">
                  <div
                    className="w-12 h-12 flex items-center justify-center border text-[11px] font-bold tracking-widest mb-7"
                    style={{ borderColor: 'rgba(201,168,76,0.5)', color: '#c9a84c', fontFamily: 'var(--font-sans)' }}
                  >
                    {company.logoInitials}
                  </div>
                  <p className="eyebrow mb-2">{company.tagline}</p>
                  <h2 className="heading-display text-[clamp(2rem,4vw,3rem)] text-[#f5f5f5] mb-4">{company.name}</h2>
                  <div className="divider-gold-left" />
                  <p className="text-[#f5f5f5]/55 text-sm leading-relaxed mt-5 max-w-md">{company.description}</p>

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-5 mt-8 mb-8">
                    {company.stats.slice(0, 4).map((stat) => (
                      <div key={stat.label}>
                        <p className="heading-display text-2xl text-gold">{stat.value}</p>
                        <p className="text-[10px] tracking-[0.15em] uppercase text-[#f5f5f5]/40 mt-0.5">{stat.label}</p>
                      </div>
                    ))}
                  </div>

                  <Link
                    to={companyPath(company.slug)}
                    className="inline-flex items-center gap-2 btn-gold-outline self-start"
                  >
                    Enter Site <ArrowRight size={12} />
                  </Link>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>
    </Layout>
  );
}
